import { useEffect, useState } from "react";
import CheckboxTree, { Node } from "react-checkbox-tree";
import "react-checkbox-tree/lib/react-checkbox-tree.css";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import ArrowRightSLineIcon from "remixicon-react/ArrowRightSLineIcon";
import ArrowDownSLineIcon from "remixicon-react/ArrowDownSLineIcon";
import AddLineIcon from "remixicon-react/AddLineIcon";
import SubtractLineIcon from "remixicon-react/SubtractLineIcon";
import { createUseStyles } from "$utils/createUseStyles";
import { theme } from "$utils/theme/bataillog";

export type JNode = {
  disabled?: boolean;
} & Node;

const treeIcons = {
  check: <CheckBoxIcon />,
  uncheck: <CheckBoxOutlineBlankIcon />,
  halfCheck: <IndeterminateCheckBoxIcon />,
  expandClose: <ArrowRightSLineIcon />,
  expandOpen: <ArrowDownSLineIcon />,
  expandAll: <AddLineIcon />,
  collapseAll: <SubtractLineIcon />,
  parentClose: "",
  parentOpen: "",
  leaf: "",
};

type Props = {
  nodes: Node[];
  /**
   * Force the Tree to check some nodes.
   *
   * If you provide this value, you must handle the node checking process by yourself.
   */
  forceCheckedNodes?: string[];
  /**
   * If true, all nodes are automatically expanded during first rendering.
   */
  expandAllNodes?: boolean;
  onCheck?: (checked: string[]) => void;
  showExpandAll?: boolean;
};

function JTree({
  nodes,
  forceCheckedNodes,
  expandAllNodes,
  onCheck = () => {},
  showExpandAll,
}: Props) {
  const [checked, setChecked] = useState([]);

  const classes = useStyles();
  const [expanded, setExpanded] = useState(() =>
    expandAllNodes
      ? nodes.flatMap((node) => [
          node.value,
          ...(node.children
            ? node.children.flatMap((child) => child.value)
            : []),
        ])
      : []
  );

  const handleOnCheck = (checked) => {
    if (!forceCheckedNodes) {
      setChecked(checked);
    }
    onCheck(checked);
  };

  const handleOnExpand = (expanded) => {
    setExpanded(expanded);
  };

  useEffect(() => {
    if (expandAllNodes) {
      const allNodes = nodes.flatMap((node) => [
        node.value,
        ...(node.children ? node.children.flatMap((child) => child.value) : []),
      ]);
      setExpanded(allNodes);
    }
  }, [expandAllNodes, nodes]);

  return (
    <div className={classes.tree}>
      <CheckboxTree
        optimisticToggle={false}
        icons={treeIcons}
        showExpandAll={showExpandAll}
        nodes={nodes}
        checked={forceCheckedNodes || checked}
        expanded={expanded}
        onExpand={handleOnExpand}
        onCheck={handleOnCheck}
      />
    </div>
  );
}
export default JTree;

const useStyles = createUseStyles(() => ({
  tree: {
    position: "relative",
    width: "100%",

    "& > .react-checkbox-tree > ol > .rct-node-parent": {
      marginBottom: theme.spacing(1),
    },
    "& .rct-options": {
      // Make +/- icons float to gain some space
      position: "absolute",
      right: 0,
      top: 0,
    },
    "& .rct-option": {
      padding: 0,
    },

    // Checkbox colors
    "& li": {
      listStyleType: "none",
      "& input[type=checkbox]:checked + .rct-checkbox": {
        color: theme.color.blueLight,
      },
      "& input[type=checkbox]:not(:checked) + .rct-checkbox": {
        color: theme.color.gray5,
      },
    },
    "& .rct-label": {
      display: "inline-block",
      overflow: "hidden",
      padding: 0,
      width: "100%",
    },
    // if a checkbox is present, title must be smaller
    "& .rct-checkbox + .rct-label": {
      width: "calc(100% - 34px)",
    },
    "& .rct-node": {
      minHeight: "28px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
    "& .react-checkbox-tree > ol > .rct-node-parent > ol > .rct-node": {
      borderLeft: `1px solid ${theme.color.gray3}`,
    },
    "& .rct-node-icon": {
      padding: 0,
    },
    "& .rct-text": {
      maxWidth: "100%",
    },
    "& label": {
      alignItems: "center",
      display: "flex",
      minWidth: "350px", // To avoid the + and - expand buttons to be on the label by pushing them to the right
      overflow: "hidden",
      width: "100%",
    },
    "& .rct-bare-label": {
      overflow: "hidden",
      width: "100%",
    },
    "& .rct-disabled > .rct-text > label": {
      opacity: 1, // reset library opacity for disabled elements
    },
    "& .rct-disabled .rct-label span": {
      opacity: 0.7, // custom library opacity for disabled elements
    },
    "& .rct-disabled button, & .rct-disabled button *": {
      opacity: "1 !important",
    },
    // Vertically center checkbox in line
    "& .rct-checkbox": {
      display: "flex",
    },
    "& ol ol": {
      paddingLeft: "34px", // align all nodes together (with and without checkboxes)
    },

    "& ol, & li": {
      maxWidth: "100%",
    },
  },
}));
